import { useSyncExternalStore } from "react";

// WHAT THIS ACCOUNT MAY DO, capability by capability (GOV-12).
//
// Both planes publish `capabilities` on /api/whoami — the names authz uses on
// the server (engine/internal/authz/capabilities.go) — and every write route
// needs exactly one of them. The console used to withhold every write on a
// single `can_respond` flag, so a responder was offered a VPN policy editor
// the server refuses, and an approver-only account could not have been offered
// the approvals queue at all. Each surface now asks for the capability its
// route needs (useResponseAuthority(capability) in features/soc/api.ts).
//
// Recorded HERE, from the one funnel every whoami answer passes through
// (lib/api.ts), rather than by each route that happens to read whoami: the
// choke, devices, fleet and VPN pages each ask whoami themselves, and a store
// only one of them fed would be empty on the others.
//
// `null` means the server published no list — an older build. It is NOT "holds
// nothing": the respond answer then decides, as it always has, because reading
// absence as a refusal would strip every authoring surface from a console
// talking to a server that simply predates capabilities.

/** The capability names, as the server spells them. */
export type Capability =
  | "read"
  | "respond"
  | "approve"
  | "policy.vpn.write"
  | "policy.detect.write"
  | "tuning.write"
  | "guardrail.write"
  | "governance.write"
  // EDGE-04, FW-02 (decision 0027): the edge's sites and the firewall's zone
  // policy, each its own capability, held by tenant-policy-author.
  | "policy.edge.write"
  | "policy.firewall.write"
  // X-05: the PROVIDER scope — what every tenant inherits. msoc-admin's
  // alone; the control plane publishes it only to a principal that holds it.
  | "administer-platform";

/** What a refusal of each authoring capability means to the operator. */
const WORK: Record<Exclude<Capability, "read" | "respond">, { work: string; role: string }> = {
  "administer-platform": {
    work: "change what every tenant inherits from the provider (baselines, detection packs, ladder templates)",
    role: "msoc-admin"
  },
  "policy.edge.write": { work: "change the sites the edge publishes", role: "tenant-policy-author" },
  "policy.firewall.write": { work: "change the firewall's zone policy", role: "tenant-policy-author" },
  approve: { work: "approve or deny another operator's request", role: "tenant-approver" },
  "policy.vpn.write": { work: "publish, promote or archive VPN access policy", role: "tenant-policy-author" },
  "policy.detect.write": { work: "load or unload a detection", role: "tenant-policy-author" },
  "tuning.write": { work: "add, renew or restore a suppression or a noise rule", role: "tenant-policy-author" },
  "guardrail.write": { work: "change the protect-lists", role: "tenant-policy-author" },
  "governance.write": {
    work: "change change control, retention, a legal hold or a notification route",
    role: "tenant-governance"
  }
};

/**
 * Why a control is withheld because this account's ROLE lacks the capability —
 * said as a permission, naming the capability and the role that carries it, so
 * the operator knows exactly what to ask for. Never "unavailable": the control
 * works and the estate is fine.
 */
export function capabilityReason(capability: Capability): string {
  if (capability === "read" || capability === "respond") {
    return `Your account does not hold ${capability}. This is a permission, not an outage.`;
  }
  const { work, role } = WORK[capability];
  return (
    `Your account's role does not include ${capability}, so it cannot ${work}. ` +
    `This is a permission, not an outage: ask an administrator for the ${role} role.`
  );
}

/** The short form, for a notice title or a button's `title`. */
export const NOT_IN_ROLE_TITLE = "Not in your role";

/**
 * The capability list a whoami document carries, or null when it carries none.
 * Only a real array of strings counts: anything else is an older or malformed
 * answer, which must fall back to the respond flag rather than read as "holds
 * nothing".
 */
export function readCapabilities(whoami: unknown): ReadonlySet<string> | null {
  if (!whoami || typeof whoami !== "object") return null;
  const raw = (whoami as Record<string, unknown>).capabilities;
  if (!Array.isArray(raw) || !raw.every((c) => typeof c === "string")) return null;
  return new Set(raw as string[]);
}

let published: ReadonlySet<string> | null = null;
const listeners = new Set<() => void>();

/** What the last whoami said, or null when no answer has carried a list. */
export function capabilitiesNow(): ReadonlySet<string> | null {
  return published;
}

export function subscribeCapabilities(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function sameSet(a: ReadonlySet<string> | null, b: ReadonlySet<string> | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

/**
 * Record what a whoami said. `null` is an answer that carried no list; the
 * snapshot object only changes when the set does, so a poll that says the same
 * thing re-renders nothing.
 */
export function recordCapabilities(next: ReadonlySet<string> | readonly string[] | null): void {
  const set = next === null ? null : next instanceof Set ? (next as ReadonlySet<string>) : new Set(next as readonly string[]);
  if (sameSet(set, published)) return;
  published = set;
  for (const listener of listeners) listener();
}

/**
 * Called by lib/api.ts for every successful response. Only /api/whoami speaks
 * for the account; the query string is ignored so a scoped whoami counts too.
 */
export function noteCapabilitiesFromResponse(path: string, body: unknown): void {
  const query = path.indexOf("?");
  if ((query === -1 ? path : path.slice(0, query)) !== "/api/whoami") return;
  recordCapabilities(readCapabilities(body));
}

/** The capability list, re-rendering when a whoami changes it. */
export function useCapabilities(): ReadonlySet<string> | null {
  return useSyncExternalStore(subscribeCapabilities, capabilitiesNow, capabilitiesNow);
}

/**
 * Whether a capability-gated control is withheld by the capability ALONE, and
 * the sentence to show: null when the list is not published (the respond flag
 * decides) or when it holds the capability. For surfaces that already carry
 * their own respond answer (the VPN page reads whoami itself).
 */
export function capabilityWithheld(capability: Capability, caps: ReadonlySet<string> | null): string | null {
  if (caps === null || caps.has(capability)) return null;
  return capabilityReason(capability);
}
